// services
import { eachLimit } from "async";

import {
  getAllRolesDocPaths,
  getObjectPath,
  getTargetDocPath,
  getTrainingInstancePath,
  getUserPath,
  getUserPermissionsDoc,
} from "wombat-global/src/constants";
import { getGlobalUserDocumentFromCache } from "../../services/firebase/getGlobalUser";

// hooks
import { precachePath } from "../offline-support/precachePath";

// types
import { UserEntity } from "wombat-global/src/typings";
import {
  getTargetFromAccessKey,
  uniqueTargetString,
} from "wombat-global/src/utilities";

const USER_TARGET_PRECACHE_CONCURRENCY = 2;
const USER_DOCUMENT_PRECACHE_CONCURRENCY = 3;

export async function precacheUsersOfflineDocuments(userId: string) {
  const userEntity = await getGlobalUserDocumentFromCache(userId);

  // set the organizations the user has access to
  await eachLimit(
    Object.keys(userEntity.access || {}),
    USER_TARGET_PRECACHE_CONCURRENCY,
    async (key) => {
      const target = getTargetFromAccessKey(key);
      // oxlint-disable-next-line no-console
      console.log("precaching", uniqueTargetString(target));
      const [, , , targetUser] = await Promise.all([
        // cache allRoles for training record views
        precachePath(getAllRolesDocPaths(target), {}, { mainThread: true }),

        // cache permissions doc for ACL checks
        precachePath(
          getUserPermissionsDoc({ target, userId }),
          {},
          { mainThread: true },
        ),

        // pre-cache all target roots the user has access too
        precachePath(getTargetDocPath(target), {}, { mainThread: true }),

        // cache user record
        precachePath<UserEntity>(
          getUserPath(target, userId),
          {},
          { mainThread: true },
        ),
      ]);

      const objectPrecacheTasks = Object.values(targetUser?.objects || {}).map(
        (objectRef) => {
          return async () => {
            await precachePath(
              getObjectPath({
                target,
                objectId: objectRef.id,
              }),
              {
                // if updatedAt equals to createdAt then the object is new ( set check property check to createAt not updateAt )
                ...(objectRef.createdAt !== objectRef.updatedAt
                  ? { updatedAt: objectRef.updatedAt }
                  : { createdAt: objectRef.createdAt }),
                // if updatedAt is not set then user createdAt prop
                ...(!objectRef.updatedAt
                  ? { createdAt: objectRef.createdAt }
                  : {}),
              },
            );
          };
        },
      );
      const trainingPrecacheTasks = Object.values(
        targetUser?.trainings || {},
      ).map((trainingRef) => {
        return async () => {
          await precachePath(
            getTrainingInstancePath({
              target,
              instanceId: trainingRef.id,
            }),
            {
              updatedAt: trainingRef.updatedAt,
              // if createdAt is not set then user createdAT prop
              ...(!trainingRef.updatedAt
                ? { createdAt: trainingRef.createdAt }
                : {}),
            },
          );
        };
      });

      await eachLimit(
        [...objectPrecacheTasks, ...trainingPrecacheTasks],
        USER_DOCUMENT_PRECACHE_CONCURRENCY,
        async (precacheDocument) => {
          await precacheDocument();
        },
      );
    },
  );
}
